import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Typography, Box, Paper, Divider, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          component={Link}
          to="/login"
          startIcon={<ArrowBack />}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Back to Login
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
          Privacy Policy
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>Effective Date:</strong> {new Date().toLocaleDateString()}
        </Typography>

        <Typography variant="body1" paragraph>
          This Privacy Policy describes how Healthcare EMR ("we," "our," or "us") collects, uses, and shares your personal information when you use our electronic medical record system and related services.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Information We Collect
        </Typography>

        <Typography variant="body1" paragraph>
          We collect information that you provide directly to us, including:
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Personal Information:</strong> Name, date of birth, contact information, and emergency contact details
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Health Information:</strong> Medical history, diagnoses, treatments, medications, and test results
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Insurance Information:</strong> Insurance provider details and policy information
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Usage Information:</strong> How you interact with our system, including login times and features used
          </Typography>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          How We Use Your Information
        </Typography>

        <Typography variant="body1" paragraph>
          We use the information we collect to:
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            Provide and maintain our healthcare services
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Process appointments and manage your care
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Communicate with you about your health and care
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Comply with legal and regulatory requirements
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Improve our services and user experience
          </Typography>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Information Sharing
        </Typography>

        <Typography variant="body1" paragraph>
          We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Healthcare Providers:</strong> Sharing information with your healthcare team for treatment purposes
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Legal Requirements:</strong> When required by law or court order
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Emergency Situations:</strong> When necessary to protect your health or safety
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Service Providers:</strong> With trusted third-party vendors who assist in operating our system
          </Typography>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Data Security
        </Typography>

        <Typography variant="body1" paragraph>
          We implement appropriate technical and organizational security measures to protect your personal information, including:
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            Encryption of data in transit and at rest
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Regular security assessments and updates
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Access controls and authentication measures
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Employee training on data protection
          </Typography>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Your Rights
        </Typography>

        <Typography variant="body1" paragraph>
          You have the right to:
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            Access and review your personal information
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Request corrections to inaccurate information
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Request deletion of your information (subject to legal requirements)
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            Opt out of certain communications
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            File a complaint if you believe your privacy rights have been violated
          </Typography>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          HIPAA Compliance
        </Typography>

        <Typography variant="body1" paragraph>
          Our system is designed to comply with the Health Insurance Portability and Accountability Act (HIPAA) and other applicable healthcare privacy laws. We maintain appropriate safeguards to protect your health information and ensure compliance with all regulatory requirements.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Changes to This Policy
        </Typography>

        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the effective date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Contact Us
        </Typography>

        <Typography variant="body1" paragraph>
          If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
        </Typography>

        <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 1, my: 2 }}>
          <Typography variant="body1" paragraph>
            <strong>Privacy Officer</strong><br />
            Healthcare EMR<br />
            Email: info@quantumleapcorp.com
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            size="large"
            startIcon={<ArrowBack />}
          >
            Return to Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
